import React, { useState } from "react";
import { C, fmt, statusLabel, text } from "../theme";

import { Card, Btn, Notice, StatusPill as Badge } from "../components/ui";

const Input = ({ label, ...props }) => {
  // Associate the label with the control (screen readers, click-to-focus).
  const id = React.useId();
  return (
    <div>
      <label htmlFor={id} style={{ fontSize: text.xs, fontWeight: 600, color: C.gray600, display: "block", marginBottom: 4 }}>{label}</label>
      {props.type === "select" ? (
        <select id={id} value={props.value} onChange={props.onChange} style={{ padding: "6px 10px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.md, fontFamily: "inherit", background: C.white, minWidth: 130 }}>
          {props.children}
        </select>
      ) : (
        <input id={id} {...props} style={{ padding: "6px 10px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.md, fontFamily: "inherit", boxSizing: "border-box", minWidth: 120, ...props.style }} />
      )}
    </div>
  );
};

const REPORT_TYPES = [
  { id: "aors", label: "Approval of Needs Requests", icon: "📋" },
  { id: "pos", label: "Purchase Orders", icon: "🚚" },
  { id: "grns", label: "Goods Receipt Notes", icon: "📦" },
  { id: "invoices", label: "Invoices", icon: "🧾" },
  { id: "budgets", label: "Budget Utilisation", icon: "💰" },
];

const AOR_STATUSES = ["DRAFT", "RETURNED", "PENDING_APPROVAL", "APPROVED", "REJECTED", "PENDING_VIREMENT", "CANCELLED"];
const PO_STATUSES = ["DRAFT", "RETURNED", "PENDING_APPROVAL", "ISSUED", "PARTIALLY_RECEIVED", "FULFILLED", "CANCELLED"];
const GRN_STATUSES = ["PARTIAL", "FULFILLED"];
const INV_STATUSES = ["DRAFT", "RETURNED", "PENDING_APPROVAL", "APPROVED", "REJECTED", "PUSHED_TO_XERO"];
const PAYMENT_STATUSES = ["UNPAID", "PARTIALLY_PAID", "PAID"];
const MATCH_STATUSES = ["MATCHED", "MISMATCH_QTY", "MISMATCH_PRICE", "UNMATCHED"];
const BUSINESS_UNITS = ["NIC", "SSIR", "SMP", "TRO", "Corporate"];
const BUS = ["NIC", "SSIR", "SMP", "TRO", "Corporate"];

// Column configs per report type
const COLUMNS = {
  aors: [
    { key: "aorNumber", label: "AOR No." },
    { key: "date", label: "Date", render: r => new Date(r.createdAt).toLocaleDateString() },
    { key: "requestor", label: "Requestor", render: r => r.requestor?.displayName },
    { key: "description", label: "Description" },
    { key: "businessUnit", label: "Business Unit" },
    { key: "budgetRef", label: "Budget Ref" },
    { key: "total", label: "Total", render: r => fmt(r.total) },
    { key: "status", label: "Status", render: r => <Badge status={r.status} /> },
  ],
  pos: [
    { key: "poNumber", label: "PO No." },
    { key: "date", label: "Date", render: r => new Date(r.createdAt).toLocaleDateString() },
    { key: "aor", label: "AOR Ref", render: r => r.aor?.aorNumber },
    { key: "vendor", label: "Vendor" },
    { key: "businessUnit", label: "Business Unit" },
    { key: "budgetRef", label: "Budget Ref" },
    { key: "total", label: "Total", render: r => fmt(r.total) },
    { key: "status", label: "Status", render: r => <Badge status={r.status} /> },
  ],
  grns: [
    { key: "grnNumber", label: "GRN No." },
    { key: "date", label: "Date", render: r => new Date(r.createdAt).toLocaleDateString() },
    { key: "po", label: "PO Ref", render: r => r.po?.poNumber },
    { key: "vendor", label: "Vendor", render: r => r.po?.vendor },
    { key: "receivedBy", label: "Received By", render: r => r.receivedBy?.displayName },
    { key: "status", label: "Status", render: r => <Badge status={r.status} /> },
  ],
  invoices: [
    { key: "invoiceNumber", label: "Invoice No." },
    { key: "date", label: "Date", render: r => new Date(r.createdAt).toLocaleDateString() },
    { key: "vendorInvNumber", label: "Vendor Inv" },
    { key: "vendor", label: "Vendor" },
    { key: "po", label: "PO Ref", render: r => r.po?.poNumber },
    { key: "total", label: "Total", render: r => fmt(r.total) },
    { key: "matchStatus", label: "Match", render: r => <Badge status={r.matchStatus} /> },
    { key: "status", label: "Status", render: r => <Badge status={r.status} /> },
    { key: "paymentStatus", label: "Payment", render: r => <Badge status={r.paymentStatus} /> },
  ],
  budgets: [
    { key: "bu", label: "BU" },
    { key: "ref", label: "Budget Ref" },
    { key: "category", label: "Category" },
    { key: "annual", label: "Annual", render: r => fmt(r.annual) },
    { key: "committedPOs", label: "Committed", render: r => fmt(r.committedPOs) },
    { key: "utilizedPOs", label: "Utilized", render: r => fmt(r.utilizedPOs) },
    { key: "approvedAORs", label: "Approved AORs", render: r => fmt(r.approvedAORs) },
    { key: "available", label: "Available", render: r => <span style={{ color: r.available < 0 ? C.danger : C.success, fontWeight: 700 }}>{fmt(r.available)}</span> },
  ],
};

export default function ReportsPage() {
  const [reportType, setReportType] = useState("aors");
  const [filters, setFilters] = useState({});
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }

  const handleSearch = async () => {
    setLoading(true);
    setSearched(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const res = await fetch("/api/reports/" + reportType + "?" + params.toString());
      const data = await res.json();
      setResults(data);
    } catch (err) {
      setNotice({ kind: "error", text: "Error: " + err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const cols = COLUMNS[reportType];
    const header = cols.map(c => c.label).join(",");
    const rows = results.map(r => cols.map(c => {
      if (["total", "annual", "committedPOs", "utilizedPOs", "approvedAORs", "available"].includes(c.key)) return r[c.key] || 0;
      if (c.key === "date") return new Date(r.createdAt).toLocaleDateString();
      if (c.key === "requestor") return r.requestor?.displayName || "";
      if (c.key === "receivedBy") return r.receivedBy?.displayName || "";
      if (c.key === "po" || c.key === "aor") return r[c.key]?.poNumber || r[c.key]?.aorNumber || "";
      if (c.key === "vendor" && reportType === "grns") return r.po?.vendor || "";
      return r[c.key] || "";
    }).join(","));

    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `SGNIC_${reportType.toUpperCase()}_Report_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleChangeType = (type) => {
    setReportType(type);
    setFilters({});
    setResults([]);
    setSearched(false);
  };

  const totalAmount = results.reduce((s, r) => s + (r.total || r.annual || 0), 0);
  const cols = COLUMNS[reportType];

  return (
    <div>
      <h1 style={{ fontSize: text.h1, fontWeight: 700, color: C.gray1100, margin: "0 0 16px" }}>Reports & Search</h1>

      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      {/* Report type selector */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {REPORT_TYPES.map(rt => (
          <div key={rt.id} onClick={() => handleChangeType(rt.id)} style={{
            padding: "10px 18px", borderRadius: 8, cursor: "pointer", fontSize: text.md, fontWeight: 600,
            border: reportType === rt.id ? `2px solid ${C.primary}` : `2px solid ${C.gray200}`,
            background: reportType === rt.id ? C.primaryBg : C.white,
            color: reportType === rt.id ? C.primary : C.gray700,
          }}>{rt.icon} {rt.label}</div>
        ))}
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 16, padding: 14 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          {/* Common: date range */}
          {reportType !== "budgets" && (
            <>
              <Input label="From Date" type="date" value={filters.dateFrom || ""} onChange={e => setFilters({ ...filters, dateFrom: e.target.value })} />
              <Input label="To Date" type="date" value={filters.dateTo || ""} onChange={e => setFilters({ ...filters, dateTo: e.target.value })} />
            </>
          )}

          {/* AOR filters */}
          {reportType === "aors" && (
            <>
              <Input label="Status" type="select" value={filters.status || ""} onChange={e => setFilters({ ...filters, status: e.target.value })}>
                <option value="">All</option>
                {AOR_STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
              </Input>
              <Input label="Business Unit" type="select" value={filters.businessUnit || ""} onChange={e => setFilters({ ...filters, businessUnit: e.target.value })}>
                <option value="">All</option>
                {BUSINESS_UNITS.map(c => <option key={c} value={c}>{c}</option>)}
              </Input>
              <Input label="Min Amount" type="number" value={filters.minAmount || ""} onChange={e => setFilters({ ...filters, minAmount: e.target.value })} style={{ width: 100 }} />
              <Input label="Max Amount" type="number" value={filters.maxAmount || ""} onChange={e => setFilters({ ...filters, maxAmount: e.target.value })} style={{ width: 100 }} />
            </>
          )}

          {/* PO filters */}
          {reportType === "pos" && (
            <>
              <Input label="Status" type="select" value={filters.status || ""} onChange={e => setFilters({ ...filters, status: e.target.value })}>
                <option value="">All</option>
                {PO_STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
              </Input>
              <Input label="Vendor" value={filters.vendor || ""} onChange={e => setFilters({ ...filters, vendor: e.target.value })} placeholder="Search vendor..." />
              <Input label="Business Unit" type="select" value={filters.businessUnit || ""} onChange={e => setFilters({ ...filters, businessUnit: e.target.value })}>
                <option value="">All</option>
                {BUSINESS_UNITS.map(c => <option key={c} value={c}>{c}</option>)}
              </Input>
            </>
          )}

          {/* GRN filters */}
          {reportType === "grns" && (
            <>
              <Input label="Status" type="select" value={filters.status || ""} onChange={e => setFilters({ ...filters, status: e.target.value })}>
                <option value="">All</option>
                {GRN_STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
              </Input>
              <Input label="PO Number" value={filters.poNumber || ""} onChange={e => setFilters({ ...filters, poNumber: e.target.value })} placeholder="e.g. PO-2026" />
            </>
          )}

          {/* Invoice filters */}
          {reportType === "invoices" && (
            <>
              <Input label="Status" type="select" value={filters.status || ""} onChange={e => setFilters({ ...filters, status: e.target.value })}>
                <option value="">All</option>
                {INV_STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
              </Input>
              <Input label="Payment" type="select" value={filters.paymentStatus || ""} onChange={e => setFilters({ ...filters, paymentStatus: e.target.value })}>
                <option value="">All</option>
                {PAYMENT_STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
              </Input>
              <Input label="Match" type="select" value={filters.matchStatus || ""} onChange={e => setFilters({ ...filters, matchStatus: e.target.value })}>
                <option value="">All</option>
                {MATCH_STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
              </Input>
              <Input label="Vendor" value={filters.vendor || ""} onChange={e => setFilters({ ...filters, vendor: e.target.value })} placeholder="Search vendor..." />
            </>
          )}

          {/* Budget filters */}
          {reportType === "budgets" && (
            <Input label="Business Unit" type="select" value={filters.bu || ""} onChange={e => setFilters({ ...filters, bu: e.target.value })}>
              <option value="">All</option>
              {BUS.map(b => <option key={b} value={b}>{b}</option>)}
            </Input>
          )}

          <Btn onClick={handleSearch} disabled={loading}>{loading ? "Searching..." : "🔍 Search"}</Btn>
          <Btn variant="outline" onClick={() => { setFilters({}); setResults([]); setSearched(false); }}>Clear</Btn>
        </div>
      </Card>

      {/* Results */}
      {searched && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: text.md, color: C.gray600 }}>
              <strong>{results.length}</strong> results found
              {totalAmount > 0 && reportType !== "grns" && <span> • Total: <strong>{fmt(totalAmount)}</strong></span>}
            </div>
            {results.length > 0 && <Btn variant="outline" onClick={handleExport}>📥 Export CSV</Btn>}
          </div>

          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ maxHeight: 500, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
                <thead><tr>{cols.map((c, i) => (
                  <th scope="col" key={i} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase", whiteSpace: "nowrap", position: "sticky", top: 0, background: C.white }}>{c.label}</th>
                ))}</tr></thead>
                <tbody>
                  {results.map((r, ri) => (
                    <tr key={ri} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                      {cols.map((c, ci) => (
                        <td key={ci} style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                          {c.render ? c.render(r) : r[c.key] || "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {results.length === 0 && (
                    <tr><td colSpan={cols.length} style={{ padding: 30, textAlign: "center", color: C.gray500 }}>No results found. Try adjusting your filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {!searched && (
        <Card style={{ textAlign: "center", padding: 40, color: C.gray500 }}>
          <div style={{ fontSize: text.metric, marginBottom: 8 }}>🔍</div>
          <div style={{ fontSize: text.lg, fontWeight: 600 }}>Select a report type and set your filters</div>
          <div style={{ fontSize: text.sm, marginTop: 4 }}>Results can be exported to CSV for use in Excel</div>
        </Card>
      )}
    </div>
  );
}